/* eslint-disable @typescript-eslint/no-explicit-any */

import { isAxiosError } from 'axios';

import { api, ApiResponse } from '@/api/api';
import { CANDIDATE_SCREENING_URLS } from '@/api/url';
import { Candidate } from '@/types/Candidate';
import { Job } from '@/types/Job';
import { Screening, ScreeningQuestion } from '@/types/Screening';

export interface ScreeningResponse {
  screening: Screening;
  candidate: Candidate;
  job: Job | null;
  practice_questions: ScreeningQuestion[];
}

/**
 * Retrieves the screening
 * @returns Promise<ApiResponse<ScreeningResponse>> - The API response containing the screening data
 * @throws Error if an unknown error occurs
 */
export const apiGetCandidateScreening = async (
  accessToken: string
): Promise<ApiResponse<ScreeningResponse>> => {
  try {
    const response = await api.get<ApiResponse<ScreeningResponse>>(CANDIDATE_SCREENING_URLS.GET, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

/**
 * Starts a screening session
 * @param accessToken - The access token for authentication
 * @returns Promise<ApiResponse<void>> - The API response containing the screening data
 * @throws Error if an unknown error occurs
 */
export const apiStartScreening = async (accessToken: string): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post<ApiResponse<void>>(
      CANDIDATE_SCREENING_URLS.START,
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

export interface SubmitScreeningAnswerPayload {
  question_id: string;
  file: File;
  retake_times: number;
  complete: boolean;
}

/**
 * Submits an answer for a screening question.
 * @param accessToken - The access token for authentication
 * @param questionId - The ID of the question being answered
 * @param file - The file to be submitted as the answer (e.g., audio, video, etc.)
 * @returns Promise<ApiResponse<void>> - The API response
 * @throws Error if an unknown error occurs
 */
export const apiSubmitScreeningAnswer = async (
  accessToken: string,
  payload: SubmitScreeningAnswerPayload
): Promise<ApiResponse<void>> => {
  try {
    const formData = new FormData();
    formData.append('question_id', payload.question_id);
    formData.append('file', payload.file);
    formData.append('retake_times', payload.retake_times.toString());
    formData.append('complete', payload.complete ? 'true' : 'false');

    const response = await api.post<ApiResponse<void>>(CANDIDATE_SCREENING_URLS.SUBMIT, formData, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

/**
 * Completes the screening session.
 * @param accessToken - The access token for authentication
 * @returns Promise<ApiResponse<void>> - The API response containing the screening data
 * @throws Error if an unknown error occurs
 */
export const apiCompleteScreening = async (accessToken: string): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post<ApiResponse<void>>(
      CANDIDATE_SCREENING_URLS.COMPLETE,
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};
